import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { RefreshCw, Loader2, AlertCircle, AlertTriangle, Link2, Printer, Check } from 'lucide-react';

const API_URL = 'https://api.glowytics.com';
const CANON_BRAND_ID = '3987361f-4006-4819-bd83-f78b5a2155ec';
const CANON_RED = '#CC0000';
const LS_KEY = 'canon-cea-overrides-v1';

const fmt = (n) => (n == null ? '—' : Math.round(n).toLocaleString('tr-TR'));
const money = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('tr-TR')} ₺`);
const pct = (n) => (n == null ? '—' : `%${n.toFixed(2)}`);
const dec = (n) => (n == null ? '—' : n.toFixed(2));

const MATCH_LABEL = {
  caption: { text: 'Caption eşleşmesi', cls: 'bg-green-100 text-green-800' },
  'campaign-name': { text: 'Kampanya adı eşleşmesi', cls: 'bg-teal-100 text-teal-800' },
  manual: { text: 'Elle eşleştirildi', cls: 'bg-blue-100 text-blue-800' },
  'no-caption': { text: "Excel'de caption yok", cls: 'bg-gray-100 text-gray-600' },
  unmatched: { text: 'Eşleşme bulunamadı', cls: 'bg-amber-100 text-amber-800' },
};

export default function CanonCeaReport() {
  const [since, setSince] = useState('2026-06-01');
  const [until, setUntil] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // hangi satırın eşleştirmesi açık

  // Elle eşleştirmeler tarayıcıda saklanır — prod'da şema değişikliği yapmamak için.
  const [overrides, setOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
  });
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(overrides)); }, [overrides]);

  const overrideParam = useMemo(
    () => Object.entries(overrides)
      .filter(([, ids]) => ids && ids.length)
      .map(([rowId, ids]) => `${rowId}:${ids.join('|')}`)
      .join(','),
    [overrides],
  );

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/canon-report/cea`, {
        params: { since, until, overrides: overrideParam || undefined },
        headers: { 'X-Brand-ID': CANON_BRAND_ID },
        withCredentials: true,
      });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [since, until, overrideParam]);

  useEffect(() => { load(); }, [load]);

  const toggleAd = (rowId, adId) => {
    setOverrides((prev) => {
      const cur = prev[rowId] || [];
      const next = cur.includes(adId) ? cur.filter((x) => x !== adId) : [...cur, adId];
      const out = { ...prev };
      if (next.length) out[rowId] = next; else delete out[rowId];
      return out;
    });
  };

  const rows = data?.rows || [];
  const matchedCount = rows.filter((r) => r.ads.length).length;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Başlık */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: CANON_RED }}>
            Canon — CEA Raporu (Gerçekleşen)
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Haziran Planı › CEA sheet, on-page boosting bloğu. Gerçekleşen rakamlar canlı Meta'dan çekilir.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="text-xs text-gray-600">
            Başlangıç
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
              className="block border rounded px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-gray-600">
            Bitiş
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
              className="block border rounded px-2 py-1 text-sm" />
          </label>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded text-white text-sm disabled:opacity-50"
            style={{ background: CANON_RED }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Yenile
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-2 px-3 py-2 rounded border text-sm">
            <Printer className="w-4 h-4" /> Yazdır
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-800 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Eşleştirme yönteminin sınırı — rapor paylaşılmadan önce görülmeli. */}
      <div className="mb-4 p-3 rounded bg-blue-50 text-blue-900 text-sm flex gap-2">
        <Link2 className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Eşleştirme <strong>caption metni</strong> üzerinden yapılır. Excel'deki Instagram linkleri
          organik postu gösterir; Meta'nın reklam creative'i ise dark post kopyasını gösterir, bu ikisi
          hiçbir zaman örtüşmez. Caption'ı olmayan satırlar otomatik eşleşemez — aşağıdan elle
          eşleştirebilirsiniz.
        </span>
      </div>

      <div className="mb-4 p-3 rounded bg-amber-50 text-amber-900 text-sm flex gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          <strong>Video satırlarında etkileşim sayısı video izlenmeleri hariç gösterilir.</strong> Bu
          reklamlar THRUPLAY'e optimize edildiği için Meta'nın <code>post_engagement</code> rakamının
          neredeyse tamamı <code>video_view</code>'dur; statik postlarla aynı sütunda gösterilse
          "etkileşim oranı %98" gibi yanıltıcı bir sonuç çıkardı.
        </span>
      </div>

      {data?.duplicates?.length > 0 && (
        <div className="mb-4 p-3 rounded bg-amber-50 text-amber-900 text-sm flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Çifte sayım uyarısı:</strong> aynı reklam birden fazla plan satırına düştü —
            toplamlar bu reklamı iki kez sayıyor.
            <ul className="mt-1 list-disc ml-5">
              {data.duplicates.map((d) => (
                <li key={d.adId}>{d.adName || d.adId} → {d.rowIds.join(', ')}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {data && (
        <div className="mb-4 text-sm text-gray-700">
          {rows.length} plan satırı · <strong>{matchedCount}</strong> tanesi eşleşti ·
          {' '}Plan toplam <strong>{money(data.totals.plan)}</strong> ·
          {' '}Gerçekleşen harcama <strong>{money(data.totals.actual.spend)}</strong>
          {' '}({data.totals.actual.adCount} reklam)
        </div>
      )}

      {/* Satırlar */}
      <div className="space-y-4">
        {rows.map((r) => {
          const badge = MATCH_LABEL[r.matchType] || MATCH_LABEL.unmatched;
          const a = r.actual;
          const over = r.spendRatio != null && r.spendRatio > 1.02;
          return (
            <div key={r.id} className="border rounded-lg overflow-hidden bg-white">
              <div className="px-4 py-3 border-b flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">{r.frame}</div>
                  <div className="font-semibold">{r.pillar}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.objective} · Plan {money(r.budget)}
                    {r.fb && ` · FB ${r.fb}`}{r.ig && ` · IG ${r.ig}`}
                    {r.flight && ` · ${r.flight}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${badge.cls}`}>
                    {badge.text}{r.matchType === 'caption' && ` (${r.matchScore.toFixed(2)})`}
                  </span>
                  <button onClick={() => setEditing(editing === r.id ? null : r.id)}
                    className="text-xs px-2 py-1 rounded border hover:bg-gray-50">
                    {editing === r.id ? 'Kapat' : 'Elle eşleştir'}
                  </button>
                </div>
              </div>

              {/* Gerçekleşen metrikler */}
              {a ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 divide-x divide-y md:divide-y-0">
                  <Metric label="Harcama" value={money(a.spend)}
                    sub={r.spendRatio != null ? `plan ${money(r.budget)}` : null} warn={over} />
                  <Metric label="Gösterim" value={fmt(a.impressions)} />
                  <Metric label="Erişim" value={fmt(a.reach)} />
                  <Metric label="Tıklama" value={fmt(a.clicks)} />
                  <Metric
                    label={a.isVideo ? 'Etkileşim (video hariç)' : 'Etkileşim'}
                    value={fmt(a.isVideo ? a.engagementExVideo : a.engagement)}
                    sub={a.isVideo ? `+${fmt(a.videoViews)} video izlenme` : null}
                  />
                  <Metric
                    label="Etkileşim Oranı"
                    value={pct(a.isVideo ? a.engagementExVideoRate : a.engagementRate)}
                    sub={a.isVideo ? `video dahil %${a.engagementRate.toFixed(1)}` : null}
                  />
                  <Metric label="CTR" value={pct(a.ctr)} />
                  <Metric label="CPM / CPC" value={`${dec(a.cpm)} / ${dec(a.cpc)}`} />
                </div>
              ) : (
                <div className="px-4 py-4 text-sm text-gray-500">
                  {r.matchType === 'no-caption'
                    ? "Excel'de bu satırın caption hücresi boş, eşleşecek metin yok. Reklamı elle seçin."
                    : `Eşleşen reklam bulunamadı (en yüksek benzerlik ${r.matchScore.toFixed(2)}). Reklamı elle seçin.`}
                </div>
              )}

              {/* Eşleşen reklamlar */}
              {r.ads.length > 0 && (
                <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-700">
                  {r.ads.map((ad) => (
                    <div key={ad.adId} className="flex justify-between gap-4 py-0.5">
                      <span className="truncate">{ad.adName}</span>
                      <span className="shrink-0 tabular-nums">
                        {money(ad.spend)} · {fmt(ad.impressions)} gst
                        {ad.instagramPermalink && (
                          <a href={ad.instagramPermalink} target="_blank" rel="noreferrer"
                            className="ml-2 text-blue-600 hover:underline">IG</a>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Elle eşleştirme paneli */}
              {editing === r.id && data && (
                <div className="border-t bg-white p-3">
                  <div className="text-xs text-gray-600 mb-2">
                    Bu plan satırına ait reklamları seçin (birden fazla seçilebilir — FB ve IG kopyaları
                    ayrı reklamdır). Seçim tarayıcınızda saklanır.
                  </div>
                  <div className="max-h-72 overflow-y-auto border rounded divide-y">
                    {data.allAds.map((ad) => {
                      const on = (overrides[r.id] || []).includes(ad.adId);
                      return (
                        <button key={ad.adId} onClick={() => toggleAd(r.id, ad.adId)}
                          className={`w-full text-left px-3 py-2 text-xs flex gap-2 items-start hover:bg-gray-50 ${on ? 'bg-blue-50' : ''}`}>
                          <span className="w-4 shrink-0 mt-0.5">{on && <Check className="w-3 h-3 text-blue-600" />}</span>
                          <span className="flex-1 min-w-0">
                            <span className="font-medium block truncate">{ad.adName}</span>
                            <span className="text-gray-500 block truncate">
                              {ad.campaignName} · {ad.start || '?'} → {ad.end || '?'}
                            </span>
                            {ad.body && (
                              <span className="text-gray-400 block truncate">{ad.body.slice(0, 110)}</span>
                            )}
                          </span>
                          <span className="shrink-0 tabular-nums text-gray-600">{money(ad.spend)}</span>
                        </button>
                      );
                    })}
                  </div>
                  {(overrides[r.id] || []).length > 0 && (
                    <button onClick={() => setOverrides((p) => { const o = { ...p }; delete o[r.id]; return o; })}
                      className="mt-2 text-xs text-red-600 hover:underline">
                      Elle eşleştirmeyi temizle (otomatiğe dön)
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loading && !data && (
        <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Meta'dan çekiliyor…
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, sub, warn }) {
  return (
    <div className="px-3 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${warn ? 'text-amber-600' : ''}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}
